import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, BookOpen } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { destinations, media } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp, PageHeader } from "@/components/ui-primitives";
import ContactForm from "@/components/ContactForm";

const VideoHero = ({ src, poster, eyebrow, title, intro }) => (
  <section className="relative min-h-[70vh] flex items-end" data-testid="destination-detail-header">
    <div className="absolute inset-0 overflow-hidden">
      <video
        key={src}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={poster}
        className="w-full h-full object-cover"
      >
        <source src={src} type="video/mp4" />
        {poster && <img src={poster} alt="" className="w-full h-full object-cover" />}
      </video>
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
    </div>
    <div className="relative px-6 sm:px-10 lg:px-16 pb-20 pt-44 w-full">
      <div className="max-w-4xl">
        <FadeUp>
          <div className="overline mb-5">{eyebrow}</div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
            {title}
          </h1>
        </FadeUp>
        {intro && (
          <FadeUp delay={0.2}>
            <p className="mt-8 text-lg text-white/85 max-w-2xl leading-relaxed">{intro}</p>
          </FadeUp>
        )}
      </div>
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
  </section>
);

const DestinationDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const dest = destinations.find((d) => d.slug === slug);
  if (!dest) return <Navigate to="/destinations" replace />;

  const copy = dest[lang];
  const fallbackImgs = [media.turkishCoast, media.marinaSunset, media.heroMain, media.crewOps];
  const heroImg = (dest.images && dest.images[0]) || fallbackImgs[destinations.indexOf(dest) % fallbackImgs.length];
  const galleryImgs = (dest.images && dest.images.slice(1, 4)) || [];
  const others = destinations.filter((d) => d.slug !== slug);

  const historyEyebrow = lang === "ru" ? "История" : lang === "tr" ? "Tarihçe" : "Heritage";
  const historyTitle = lang === "ru" ? "Гавань с памятью" : lang === "tr" ? "Hafızası olan bir liman" : "A harbour with memory";
  const galleryEyebrow = lang === "ru" ? "Галерея" : lang === "tr" ? "Galeri" : "Gallery";

  return (
    <>
      <SEO
        path={`/destinations/${slug}`}
        title={copy.name}
        description={copy.intro}
      />
      {dest.heroVideo ? (
        <VideoHero
          src={dest.heroVideo}
          poster={heroImg}
          eyebrow={copy.tagline}
          title={copy.name}
          intro={copy.intro}
        />
      ) : (
        <PageHeader
          eyebrow={copy.tagline}
          title={copy.name}
          intro={copy.intro}
          image={heroImg}
          testId="destination-detail-header"
        />
      )}

      <section className="py-24 px-6 sm:px-10 lg:px-16" data-testid="destination-detail-body">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8">
            <FadeUp>
              <div className="overline mb-5">{t.common.whatWeHandle}</div>
              <h2 className="font-display text-3xl sm:text-4xl text-navy mb-8">
                {lang === "ru" ? "Операционные возможности" : lang === "tr" ? "Operasyonel kapasite" : "Operational capability"}
              </h2>
              <ul className="space-y-4">
                {copy.ops.map((op, i) => (
                  <li key={i} className="flex items-start gap-4 border-b border-navy/10 pb-4">
                    <Check size={16} className="text-brand mt-1 shrink-0" strokeWidth={1.5} />
                    <span className="text-navy/80 text-base leading-relaxed">{op}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>

            {copy.history && (
              <FadeUp>
                <div className="mt-16" data-testid="destination-history">
                  <div className="flex items-center gap-3 mb-5">
                    <BookOpen size={16} className="text-brand" strokeWidth={1.5} />
                    <span className="overline">{historyEyebrow}</span>
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl text-navy mb-6 leading-tight">
                    {historyTitle}
                  </h2>
                  <p className="text-navy/80 text-base sm:text-[17px] leading-[1.85] first-letter:font-display first-letter:text-5xl first-letter:text-brand first-letter:float-left first-letter:mr-2 first-letter:leading-none">
                    {copy.history}
                  </p>
                </div>
              </FadeUp>
            )}

            <FadeUp>
              <Link
                to="/destinations"
                className="mt-12 inline-flex items-center gap-2 text-brand text-[11px] tracking-[0.25em] uppercase hover:gap-3 transition-all"
              >
                <ArrowLeft size={12} /> {t.common.allDestinations}
              </Link>
            </FadeUp>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="glass p-6">
                <div className="overline mb-4">{t.common.getInTouch}</div>
                <p className="text-navy/80 text-sm mb-6">
                  {lang === "ru"
                    ? "Планируете заход в этот порт? Мы подготовим всё до вашего прибытия."
                    : lang === "tr"
                    ? "Bu limana mı geliyorsunuz? Siz varmadan her şeyi hazırlarız."
                    : "Calling this port? We'll prepare everything before you arrive."}
                </p>
                <Link
                  to="/contact"
                  state={{ location: copy.name }}
                  className="block text-center bg-brand text-white py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors"
                  data-testid="destination-detail-cta"
                >
                  {t.nav.requestSupport}
                </Link>
              </div>

              <div className="glass-light p-6">
                <div className="overline mb-4">{t.common.allDestinations}</div>
                <ul className="space-y-3">
                  {others.map((o) => (
                    <li key={o.slug}>
                      <Link
                        to={`/destinations/${o.slug}`}
                        className="flex items-center justify-between text-navy/80 hover:text-brand text-sm group"
                      >
                        <span>{o[lang].name}</span>
                        <ArrowRight size={14} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {galleryImgs.length > 0 && (
        <section className="px-6 sm:px-10 lg:px-16 pb-24" data-testid="destination-gallery">
          <FadeUp>
            <div className="overline mb-6">{galleryEyebrow}</div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {galleryImgs.map((src, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <figure
                  className="relative overflow-hidden bg-navy/5 group"
                  style={{ aspectRatio: "4 / 3" }}
                  data-testid={`gallery-${i}`}
                >
                  <img
                    src={src}
                    alt={`${copy.name} — ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                    width="800"
                    height="600"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </figure>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      <section className="py-24 bg-cream border-t border-navy/10 px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-5">{t.common.getInTouch}</div>
            <h2 className="font-display text-4xl sm:text-5xl text-navy leading-tight">
              {t.contactPage.formTitle}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
};

export default DestinationDetail;
